import { AppRoutes } from '@consts/routes';
import { Buffer } from 'buffer';
import { SCOPE_KEYS, PERMISSION_KEYS, Permission, UserType } from '@consts/permissions';

import type {
    ScopeType,
    Scopes,
    Permissions,
    RoleMap,
    RoleUtils,
    PermissionUtils,
    ScopeUtils,
    RoleStore,
} from '@/types/permissions';
import type { CognitoParsedToken } from '@/types/cognito';

export const parseToken = (token: string) => {
    if (!token) return null;
    const idPayload = Buffer.from(token.split('.')[1], 'base64');
    const userAttr = JSON.parse(idPayload.toString('utf-8'));
    return userAttr;
};

export const isAdmin = (token: string) => {
    const userAttr = parseToken(token);
    return (
        userAttr['custom:Companies'] === '*' &&
        userAttr['custom:Resellers'] === '*' &&
        (userAttr['custom:Locations'] === '*' || userAttr['custom:Locations'] === '')
    );
};

/**
 * Checks if a user is a superuser.
 * Superuser = all permissions available
 * and has all scopes
 */
export const isSuperUser = (token: string) => {
    const roles = parseRoles(parseToken(token));
    return (
        roles.role.isAdmin &&
        Object.values(roles.map.permissions).every((perm) => perm === Permission.Edit) &&
        roles.map.loggedIn
    );
};

export const getPagePermissions = (role: RoleUtils): Record<string, boolean> => {
    if (!role.loggedIn) {
        return {
            ...Object.values(AppRoutes).reduce((acc, routeName) => {
                acc[routeName] = false;
                return acc;
            }, {} as Record<string, boolean>),
            [AppRoutes.Login]: true,
            [AppRoutes.ResetPassword]: true,
            [AppRoutes.Tickets]: true,
        };
    }

    return {
        ...Object.values(AppRoutes).reduce((acc, routeName) => {
            acc[routeName] = true;
            return acc;
        }, {} as Record<string, boolean>),
        [AppRoutes.Resellers]: Boolean(role.resellers.scope.length),
        [AppRoutes.Users]: role.users.edit,
        [AppRoutes.ManageUser]: role.users.edit,
        [AppRoutes.Models]: role.uploading.edit,
        [AppRoutes.Manufacturers]: role.uploading.edit,
        [AppRoutes.Coverages]: role.uploading.edit,
        [AppRoutes.Design]: role.uploading.edit,
        [AppRoutes.BatchProcessing]: role.prices.edit && role.isAdmin,
        [AppRoutes.Organizations]: role.stores.edit,
        [AppRoutes.Invoices]: role.prices.edit && role.isAdmin,
        [AppRoutes.BillingChanges]: role.prices.edit && role.isAdmin,
        [AppRoutes.StationMoveActivity]: role.isAdmin, // system management claims implied server-side
        [AppRoutes.AdminAuditLog]: role.isSuperAdmin,
    };
};

/**
 * Will check if a token has access to a route
 */
export const checkPathHasPermission = (role: RoleUtils, route: AppRoutes): boolean => {
    const permissions = getPagePermissions(role);
    return Boolean(permissions[route]);
};

/**
 * @returns A permission type. Always an integer.
 */
const parsePermission = (
    parsedToken: CognitoParsedToken | null,
    key: (typeof PERMISSION_KEYS)[keyof typeof PERMISSION_KEYS]
) => {
    const value = parsedToken?.[key];
    // Default to None if there is no value, or if the value's number equivalent isn't
    // accounted for in the front-end's current "Permission" enum
    if (!value || !Object.values(Permission).includes(+value)) return Permission.None;
    return +value as Permission;
};

/**
 * @returns Either a `"*"` to represent a full scope,
 * or an array of int IDs that can be accessed for the
 * specified scope.
 */
const parseScope = (
    parsedToken: CognitoParsedToken | null,
    key: (typeof SCOPE_KEYS)[keyof typeof SCOPE_KEYS]
): ScopeType => {
    const value = parsedToken?.[key];
    // If no value or wrong type, no scope
    if (typeof value !== 'string') return [];

    // * The old isAdmin() util treated empty custom:Locations
    // * claim ('') as a wildcard.
    if (value === '') return '*';

    // If star, that's the type
    if (value === '*') return value;

    const split = value.split(',').map(Number);
    // Handle corrupt, return no scope
    if (split.some((val) => Number.isNaN(val))) return [];

    return split;
};

/**
 * @returns A parsed {@link CognitoParsedToken} into a digestible format to make
 * role handling much simpler.
 */
export const parseRoles = (payload: CognitoParsedToken | null): RoleStore => {
    const map = {
        scopes: Object.entries(SCOPE_KEYS).reduce((acc, [key, cognitoKey]) => {
            acc[key as keyof typeof SCOPE_KEYS] = parseScope(payload, cognitoKey);
            return acc;
        }, {} as Scopes),
        permissions: Object.entries(PERMISSION_KEYS).reduce((acc, [key, cognitoKey]) => {
            acc[key as keyof typeof PERMISSION_KEYS] = parsePermission(payload, cognitoKey);
            return acc;
        }, {} as Permissions),
        loggedIn: Boolean(payload),
    };

    const role = createRoleUtils(map);

    return {
        map,
        role,
    };
};

const getUserType = ({ isAdmin, perms }: { isAdmin: boolean; perms: PermissionUtils }) => {
    return isAdmin && perms.prices.edit
        ? UserType.Finance
        : isAdmin && perms.users.edit
        ? UserType.Support
        : UserType.User;
};

export const createRoleUtils = (map: RoleMap): RoleUtils => {
    const perms = Object.keys(map.permissions).reduce((acc, k) => {
        const key = k as keyof typeof PERMISSION_KEYS;

        const isEdit = map.permissions[key] === Permission.Edit;

        acc[key] = {
            none: map.permissions[key] === Permission.None,
            view: isEdit || map.permissions[key] === Permission.View,
            edit: isEdit,
        };
        return acc;
    }, {} as PermissionUtils);

    const scopes = Object.keys(map.scopes).reduce((acc, k) => {
        const key = k as keyof typeof SCOPE_KEYS;

        acc[key] = {
            fullAccess: map.scopes[key] === '*',
            scope: map.scopes[key],
        };

        return acc;
    }, {} as ScopeUtils);

    const isAdmin = Object.values(scopes).every(({ fullAccess }) => fullAccess);
    const name = getUserType({ isAdmin, perms });
    const isSuperAdmin = isAdmin && Object.values(perms).every(({ edit }) => edit);

    return {
        ...perms,
        ...scopes,
        isAdmin,
        isSuperAdmin,
        loggedIn: map.loggedIn,
        type: {
            name,
            user: name === UserType.User,
            finance: name === UserType.Finance,
            support: name === UserType.Support,
            admin: isAdmin,
        },
    };
};
